import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Palette, Sun, Moon, X } from '@phosphor-icons/react';
import { Checkbox } from '../../components/ui/checkbox';
import { IconToggleGroup } from '../../components/ui/IconToggleGroup';
import './WidgetThemeControls.css';

const THEME_MODE_OPTIONS = [
  { value: 'light', icon: Sun, ariaLabel: 'Light theme' },
  { value: 'dark', icon: Moon, ariaLabel: 'Dark theme' },
];

/**
 * WidgetThemeControls — плавающая иконка рядом с виджетом; по клику
 * открывается поповер с настройками ВИДЖЕТА (#1333, план #1330).
 *
 * Бренд-цвет отсюда убран намеренно: свотчи показывали пресеты Andy's Motors
 * и Autodis, то есть темы конкретных клиентов. Витрине ShiftGears рекламировать
 * чужие бренды незачем — виджет здесь всегда в своём цвете, а настраиваются
 * тема и эффекты поверхности.
 *
 * ⛔ Контрол не трогает `documentElement` и вообще ничего на странице. Первая
 * версия писала `--theme-primary` в inline-стиль корня, из-за чего
 * перекрашивалась вся витрина — кнопки, ссылки, карточки. Это была ошибка:
 * дилеру показывают, что настраивается ассистент, а не что сайт умеет менять
 * палитру. Теперь значения уходят наверх через `onChange`, `App.jsx` держит их
 * в state и передаёт единственным пропом `theme` в `VoiceWidgetConnected`.
 *
 * Лежит в `pages/shared/`, а не в `components/`: `bootstrap.jsx` инлайнит в
 * Shadow DOM дилера глоб `components/**\/*.css` целиком, и демо-онли контрол
 * иначе уехал бы в бандл каждому клиенту.
 */
export const WidgetThemeControls = ({ mode, accent, glass, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Клик вне и Esc — обычное поведение поповера. Слушатели вешаем только пока
  // он открыт, чтобы на закрытом контроле не висело двух глобальных хендлеров.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);

  return (
    <div className="widget-theme-controls" ref={rootRef}>
      {isOpen && (
        <div
          className="widget-theme-controls__popover"
          role="dialog"
          aria-label="Assistant appearance"
        >
          <div className="widget-theme-controls__head">
            <span className="widget-theme-controls__title">Assistant appearance</span>
            <button
              type="button"
              className="widget-theme-controls__close"
              onClick={close}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="widget-theme-controls__field">
            <span className="widget-theme-controls__label">Theme</span>
            <IconToggleGroup
              value={mode}
              onChange={(next) => onChange({ mode: next })}
              options={THEME_MODE_OPTIONS}
              ariaLabel="Theme"
            />
          </div>

          {/* Без подзаголовка и пояснений: подписи чекбоксов и так называют
              сами себя, а поповер узкий — лишний текст в нём переносился в
              три строки и распирал колонку. */}
          <div className="widget-theme-controls__field">
            <Checkbox
              id="wtc-accent"
              checked={accent}
              onCheckedChange={(next) => onChange({ accent: next })}
              label="Bright"
            />
            <Checkbox
              id="wtc-glass"
              checked={glass}
              onCheckedChange={(next) => onChange({ glass: next })}
              label="Glass"
            />
          </div>
        </div>
      )}

      <button
        type="button"
        className="widget-theme-controls__trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-label="Assistant appearance"
        title="Assistant appearance"
      >
        <Palette size={20} weight="bold" />
      </button>
    </div>
  );
};

WidgetThemeControls.propTypes = {
  /** Тема виджета: `light` — белый, `dark` — чёрный. */
  mode: PropTypes.oneOf(['light', 'dark']).isRequired,
  /** Accent: фоном свёрнутой пилюли становится бренд-цвет вместо стекла. */
  accent: PropTypes.bool.isRequired,
  /** Glass: полупрозрачная поверхность с блюром. */
  glass: PropTypes.bool.isRequired,
  /** Отдаёт наверх изменённое поле: `{ mode }` / `{ accent }` / `{ glass }`. */
  onChange: PropTypes.func.isRequired,
};

export default WidgetThemeControls;
